import { ActionIcon, Button, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useAddCollector, usePersonCollectors, useRemoveCollector } from "./collector-queries";

/**
 * Who may collect a child (#174, ADR 0042 §6): household adults by default, plus the people the church names, as
 * words. Shown on a child's record only; a reader without people.read_minors never reaches it (the query refuses).
 */
export function CollectorsSection({ personId, canWrite }: { personId: string; canWrite: boolean }) {
  const collectors = usePersonCollectors(personId, true);
  const add = useAddCollector();
  const remove = useRemoveCollector();
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  if (collectors.isError || collectors.data === undefined) return null;
  const code = (add.error as { code?: string } | null)?.code;
  return (
    <section aria-label="Allowed to collect">
      <Title order={4}>Allowed to collect</Title>
      <Text size="xs" c="dimmed" mt={2}>
        Shown to the person at the door at check-out. Household adults are on the list by default.
      </Text>
      <Stack gap={4} mt="xs">
        {collectors.data.household.map((c) => (
          <Text key={`h-${c.name}`} size="sm">
            {c.name}{" "}
            <Text span size="xs" c="dimmed">
              household
            </Text>
          </Text>
        ))}
        {collectors.data.named.map((c) => (
          <Group key={c.id} gap="xs" wrap="nowrap">
            <Text size="sm">
              {c.name}
              {c.relationship === null ? null : (
                <Text span size="xs" c="dimmed">
                  {" "}
                  {c.relationship}
                </Text>
              )}
            </Text>
            {canWrite ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={`Remove ${c.name} from the list`}
                loading={remove.isPending}
                onClick={() => {
                  remove.mutate({ personId, collectorId: c.id });
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null}
          </Group>
        ))}
        {collectors.data.household.length === 0 && collectors.data.named.length === 0 ? (
          <Text size="sm" c="dimmed">
            Nobody yet: no household adults, and nobody named.
          </Text>
        ) : null}
      </Stack>
      {canWrite ? (
        <Group gap="xs" align="flex-end" mt="xs" wrap="nowrap">
          <TextInput
            label="Name"
            size="xs"
            placeholder="Ngozi Okafor"
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
            }}
            style={{ flex: 1 }}
          />
          <TextInput
            label="Relationship"
            size="xs"
            placeholder="grandmother"
            value={relationship}
            onChange={(e) => {
              setRelationship(e.currentTarget.value);
            }}
            w={140}
          />
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            disabled={name.trim() === ""}
            loading={add.isPending}
            onClick={() => {
              add.mutate(
                { personId, name, relationship },
                {
                  onSuccess: () => {
                    setName("");
                    setRelationship("");
                  },
                },
              );
            }}
          >
            Add
          </Button>
        </Group>
      ) : null}
      {add.isError ? (
        <Text size="xs" c="red" mt={4}>
          {code === "23505"
            ? "That name is already on the list."
            : "Not added. Check your access and try again."}
        </Text>
      ) : null}
    </section>
  );
}
